import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { API, axios } from "@/api";
import { BottomNav } from "@/components/shared/Navigation";
import { Trophy, Loader2 } from "lucide-react";
import { trackPageView } from "@/lib/track";
import TooltipOverlay from "@/components/shared/TooltipOverlay";

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "year", label: "This Year" },
  { key: "all", label: "All Time" },
];

export default function LeaderboardPage() {
  const [entries, setEntries] = useState([]);
  const [period, setPeriod] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackPageView("leaderboard");
    (async () => {
      setLoading(true);
      try { const res = await axios.get(`${API}/leaderboard?period=${period}`); setEntries(res.data); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [period]);

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="leaderboard-page">
      <div className="bg-gradient-to-br from-amber-400 via-amber-500 to-orange-400 text-white p-6">
        <h1 className="text-xl font-bold flex items-center gap-2"><Trophy className="w-6 h-6" />Leaderboard</h1>
        <p className="text-amber-100 text-sm mt-1">Top birthday deal verifiers</p>
      </div>
      <div className="px-4 mt-4">
        <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
          {PERIODS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setPeriod(key)}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-all whitespace-nowrap ${period === key ? "bg-gradient-to-r from-amber-400 to-amber-500 text-white shadow-md" : "bg-white text-slate-600 border border-slate-200"}`}
              data-testid={`period-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
        {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div> : (
          <div className="space-y-2">
            {entries.map((entry, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }} className={`bg-white rounded-xl p-4 shadow-sm border flex items-center gap-3 ${i < 3 ? "border-amber-200" : "border-slate-100"}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${i === 0 ? "bg-gradient-to-br from-amber-400 to-amber-500 text-white" : i === 1 ? "bg-gradient-to-br from-slate-300 to-slate-400 text-white" : i === 2 ? "bg-gradient-to-br from-amber-600 to-amber-700 text-white" : "bg-slate-100 text-slate-500"}`}>{entry.rank}</div>
                <div className="flex-1"><p className="font-semibold text-sm text-slate-800">{entry.user_name}</p><p className="text-xs text-slate-400">{entry.city} · {entry.level}</p></div>
                <div className="text-right"><span className="text-lg font-bold text-amber-600">{entry.score}</span><span className="text-xs text-slate-400 block">pts</span></div>
              </motion.div>
            ))}
            {entries.length === 0 && <div className="text-center py-10 text-slate-400"><Trophy className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium">No entries yet for this period</p><p className="text-sm mt-1">Be the first to verify an offer!</p></div>}
          </div>
        )}
      </div>
      <BottomNav />
      <TooltipOverlay screen="leaderboard" targetSelector='[data-testid="period-all"]' message="See who's leading! Filter by time period to track the top verifiers." />
    </div>
  );
}
